import { Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import {
  CardTypeCallToActions,
  EnrollmentMessage,
  Learning_Type,
  RouteTypes,
} from '@app/enums/_index';
import {
  DoceboToken,
  EnrolledSessionDates,
  EnrolledSessions,
  Enrollment,
  LMSSubscription,
  User,
} from '@app/models/_index';

import { EnrollmentService } from '@app/services/_index';
import { UserService } from '@app/services/user/user.service';
import { environment } from '@environments/environment';
import { Subscription, firstValueFrom } from 'rxjs';
import { ModalDialogComponent } from '../shared/modal-dialog/modal-dialog.component';

@Component({
  selector: 'app-dashboard-progress',
  templateUrl: './dashboard-progress.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./dashboard-progress.component.scss'],
  standalone: false,
})
export class DashboardProgressComponent implements OnInit, OnDestroy {
  environment: any; // Need this for template to provide base url to an external link (ex. docebo user profile)
  toggleDropdown = '';
  sessionEnrollErrorMessage: {};
  courseId: number;
  sessionId: number;
  sessionName: string;
  @ViewChild('modalDialog') modalDialog: ModalDialogComponent;
  @Input() currentUser: User;
  @Input() isLoading: boolean;

  accessToken: DoceboToken;
  private subs: Subscription[] = [];
  constructor(private userService: UserService, private enrollmentService: EnrollmentService) {}

  /**
   * On Init, call get enrollments to load enrollments and load any user active subscriptions
   */
  async ngOnInit(): Promise<void> {
    this.environment = environment;

    this.accessToken = await firstValueFrom(this.userService.getDoceboAccessToken());
  }

  ngOnDestroy(): void {
    this.subs?.forEach((sub) => sub.unsubscribe());
  }

  /**
   * TODO: Fix me
   * Redirect and open enrollment link in Docebo url
   * @param enrollment - user enrollment in a course or subscription
   * @returns void
   */
  redirectUrl(enrollment: Enrollment): void {
    if (enrollment.type === Learning_Type.CERTIFICATION_EXAM) {
      // TODO: Redirect to Course Detail Page
      // this.courseService.getSlugByCourseId(enrollment.id)
      // .then(slug => {
      //     window.open(`course/${slug}`, '_self');
      // });
    } else {
      // Redirect to Actual Docebo Course Page
      window.open(`${this.environment.docebo.url}/${enrollment.url}`);
    }
    return;
  }

  /**
   * Toggle Navigation
   * @param subscription - the subscription selected to toggle accordion
   */
  onToggleNavigation(subscription: string): void {
    if (subscription === this.toggleDropdown) {
      this.toggleDropdown = '';
    } else {
      this.toggleDropdown = subscription;
    }
  }

  /**
   * Add Track By Enrollment Id to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of enrollment array
   * @param item - Enrollment item
   * @returns enrollment id
   */
  trackByEnrollmentId(index: number, item: Enrollment): number {
    if (item) {
      return item.id;
    } else {
      return index;
    }
  }

  /**
   * Open Unenroll Session Modal if user successfully unenroll into a course session
   * for Instructor-Led courses.
   * This modal is custom since it needs to show to the user the steps
   * to confirm their successful enrollment.
   * @param isClicked - handle event emited from couse session card
   * @param courseId - courseId for the given course session to be enrolled
   * @param sessionId - sessionId needed to be enrolled for a given course id
   */
  openUnenrollSessionModal(isClicked: boolean, courseId: number, session: any) {
    if (isClicked) {
      this.courseId = courseId;
      this.sessionId = session.id;
      this.sessionName = session.name;
      this.modalDialog.openModal();
    }
  }

  /**
   * Unenroll the session
   * This will run when you confirm to unenroll
   */
  unEnrollSession() {
    this.enrollmentService
      .unEnrollSession(this.currentUser.id, this.courseId, this.sessionId)
      .then((response: any) => {
        if (response && response.data && response.data.success == true) {
          this.userService.loadEnrollments(this.currentUser);
        } else {
          // Show unenrollment errors in UI if any
          const errorObj = { idSession: this.sessionId, errorMessage: response.data.errors[0] };
          this.sessionEnrollErrorMessage = errorObj;
        }
      })
      .catch((error) => {
        const errorObj = { idSession: this.sessionId, errorMessage: error.message };
        this.sessionEnrollErrorMessage = errorObj;
      });
  }

  /**
   * Add Track By Subscription Id to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of enrollment array
   * @param subscription - Subscription item
   * @returns enrollment id
   */
  trackBySubscriptionId(index: number, subscription: LMSSubscription): number {
    if (subscription) {
      return subscription.id;
    }
    return index;
  }

  /**
   * Add Track By Session Id to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of session array
   * @param session - Session item
   * @returns session id
   */
  trackBySessionId(index: number, session: EnrolledSessions): number {
    if (session) {
      return session.id;
    } else {
      return index;
    }
  }

  /**
   * Add Track By Date index to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of date array
   * @param date - Enrolled session date item
   * @returns index
   */
  trackByDateIndex(index: number, date: EnrolledSessionDates): number {
    // NOTE: Enrolled Session name and dates are not unique since there can be multiple sessions with the same values
    // idClassRoom and idLocation can be null or undefined and is not required, so those are not unique or valid
    return index;
  }

  /**
   *  EnrollmentMessage enums based on message types
   */
  public get enrollmentMessage(): typeof EnrollmentMessage {
    return EnrollmentMessage;
  }

  /**
   *  RouteTypes enums based on route types
   */
  public get routeTypes(): typeof RouteTypes {
    return RouteTypes;
  }

  /**
   *  CardTypeCallToActions enums based on Action
   */
  public get cardTypeCallToActions(): typeof CardTypeCallToActions {
    return CardTypeCallToActions;
  }
}
